import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  TextField, 
  Button,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import styled from '@emotion/styled';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Styled components
const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#101010',
  position: 'relative',
}));

const FooterTop = styled(Box)(({ theme }) => ({
  backgroundColor: '#101010',
  padding: '60px 0 40px',
  [theme.breakpoints.down('md')]: {
    padding: '40px 0 30px',
  }
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  backgroundColor: '#101010',
  padding: '20px 0',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
}));

const FooterLogo = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  fontSize: '24px',
  fontWeight: 'bold',
  marginBottom: '20px',
  position: 'relative',
  paddingBottom: '8px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: '2px',
    width: '40px',
    backgroundColor: '#bb0b0b',
  }
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  opacity: 0.8,
  marginBottom: '30px',
  fontSize: '14px',
  lineHeight: 1.6,
  maxWidth: '300px',
}));

const FooterSocial = styled(Box)(({ theme }) => ({
  display: 'flex',
  '& .MuiIconButton-root': {
    backgroundColor: 'transparent',
    color: '#ffff',
    marginRight: '15px',
    padding: '8px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#bb0b0b',
    }
  }
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  marginBottom: '20px',
  position: 'relative',
  paddingBottom: '8px',
  fontSize: '18px',
  fontWeight: 600,
  letterSpacing: '0.5px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: '2px',
    width: '40px',
    backgroundColor: '#bb0b0b',
  }
}));

const ServiceLink = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  opacity: 0.8,
  marginBottom: '12px',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#bb0b0b',
    opacity: 1,
  }
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '15px',
  '& .MuiSvgIcon-root': {
    color: '#bb0b0b',
    marginRight: '12px',
    marginTop: '2px',
    fontSize: '18px',
  }
}));

const ContactText = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  opacity: 0.8,
  fontSize: '14px',
  lineHeight: 1.5,
}));

const NewsletterText = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  opacity: 0.8,
  marginBottom: '20px',
  fontSize: '14px',
  lineHeight: 1.6,
}));

const NewsletterForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
}));

const EmailInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#bb0b0b',
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255, 255, 255, 0.7)',
    opacity: 1,
  }
})); 

const SubscribeButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffff',
  padding: '12px 30px',
  borderRadius: '0',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#d30000',
  },
  '&.Mui-disabled': {
    backgroundColor: '#bb0b0b',
    color: '#ffff'
  }
}));

const CopyrightText = styled(Typography)(({ theme }) => ({
  color: '#ffff',
  opacity: 0.7,
  textAlign: 'center',
  fontSize: '14px',
}));

function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubscribe = () => {
    if (!email) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Newsletter subscription:', email);
      setEmail('');
      setIsSubmitting(false);
      setOpenSnackbar(true);
    }, 1000);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <>
      <FooterSection>
        <FooterTop>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              {/* Company Info */}
              <Grid item xs={12} md={3}>
                <FooterLogo>
                  HKMSP
                </FooterLogo>
                <FooterText>
                  Delivering professional cybersecurity solutions worldwide, safeguarding digital infrastructures with expertise, vigilance, and integrity
                </FooterText>
                <FooterSocial>
                  <IconButton aria-label="LinkedIn">
                    <LinkedInIcon />
                  </IconButton>
                </FooterSocial>
              </Grid>

              {/* Services */}
              <Grid item xs={12} md={3}>
                <FooterTitle>
                  SERVICES
                </FooterTitle>
                <ServiceLink>Penetration Testing</ServiceLink>
                <ServiceLink>Security Assessments</ServiceLink>
                <ServiceLink>Managed Security Services</ServiceLink>
                <ServiceLink>Incident Response</ServiceLink>
                <ServiceLink>Security Training</ServiceLink>
              </Grid>

              {/* Contact Info */}
              <Grid item xs={12} md={3}>
                <FooterTitle>
                  CONTACT US
                </FooterTitle>
                <ContactItem>
                  <LocationOnIcon />
                  <ContactText>
                    Unit 2A, 17/F, Glenealy Tower, No.1 Glenealy<br />
                    Central, Hong Kong S.A.R
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <PhoneIcon />
                  <ContactText>(+852) 5423 3701</ContactText>
                </ContactItem>
                <ContactItem>
                  <EmailIcon />
                  <ContactText>info@hkmsp.com</ContactText>
                </ContactItem>
                <ContactItem>
                  <AccessTimeIcon />
                  <ContactText>Office Hours: 9AM - 7PM HKT</ContactText>
                </ContactItem>
              </Grid>

              {/* Newsletter */}
              <Grid item xs={12} md={3}>
                <FooterTitle>
                  NEWSLETTER
                </FooterTitle>
                <NewsletterText>
                  Stay up to update with our latest news and products.
                </NewsletterText>
                <NewsletterForm>
                  <EmailInput
                    fullWidth
                    variant="outlined"
                    placeholder="Your email address"
                    value={email}
                    onChange={handleEmailChange}
                    type="email"
                  />
                  <SubscribeButton 
                    variant="contained"
                    onClick={handleSubscribe}
                    disabled={isSubmitting || !email}
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
                  </SubscribeButton>
                </NewsletterForm>
              </Grid>
            </Grid>
          </Container>
        </FooterTop>
        
        <FooterBottom>
          <Container maxWidth="lg">
            <CopyrightText variant="body2">
              2025 HK Managed Security Provider. All Rights Reserved.
            </CopyrightText>
          </Container>
        </FooterBottom>
      </FooterSection>
      
      {/* Success message */}
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Thank you for subscribing to our newsletter!
        </Alert>
      </Snackbar>
    </>
  );
}

export default Footer;